import type {
  Account,
  Analytics,
  Capability,
  DeprecatedData,
  EnvironmentData,
  Permission,
  Plan,
  ProductEnvironment,
  User,
  ThemeName,
  AssetVisibility,
  UnicornApi,
} from '@cld/unicorn-api';
import type { AbTestingClient } from '../../api/createAbTestingClient';
import type { DispatchAction } from '../../state/actions';
import type { SubscribeToStateChanges, UnsubscribeFromStateChanges } from '../../state/middleware/subscriptionMiddleware';
import type { CloudinaryFetchClient, ConsoleLogger, StyledComponentsThemeObject } from '../../state/types';
import type { ExternalLoggerFactory } from '../../api/createExternalLoggerFactory';

export const createUnicornApi = ({
  analytics,
  abTestingClient,
  createCloudinaryFetchClient,
  dispatch,
  environmentData,
  subscribeToStateChanges,
  unsubscribeFromStateChanges,
  account,
  capabilities,
  permissions,
  productEnvironment,
  deprecatedData,
  user,
  plan,
  consoleLogger,
  externalLoggerFactory,
  assetVisibility,
  theme,
  impersonationActive,
}: {
  analytics: Analytics;
  abTestingClient: AbTestingClient;
  createCloudinaryFetchClient: UnicornApi['methods']['createFetchClient'];
  dispatch: DispatchAction;
  environmentData: EnvironmentData;
  assetVisibility: AssetVisibility;
  subscribeToStateChanges: SubscribeToStateChanges;
  unsubscribeFromStateChanges: UnsubscribeFromStateChanges;
  account: Account;
  capabilities: Capability[];
  productEnvironment: ProductEnvironment;
  permissions: Permission[];
  deprecatedData: DeprecatedData;
  user: Omit<User, 'impersonationActive'>;
  plan: Plan;
  consoleLogger: ConsoleLogger;
  externalLoggerFactory: ExternalLoggerFactory;
  theme: { name: ThemeName; styledComponentsThemeObject: StyledComponentsThemeObject };
  impersonationActive: boolean;
}): UnicornApi => {
  const changeProductEnvironment: UnicornApi['methods']['changeProductEnvironment'] = (newExternalId) =>
    new Promise((resolve) => {
      dispatch({ type: 'productEnvironmentChangeQueued', payload: { newExternalId } });

      subscribeToStateChanges(function subscriber(state) {
        if (state.applicationStatus === 'ready' && state.productEnvironmentChangeState.status === 'idle') {
          unsubscribeFromStateChanges(subscriber);
          resolve();
        }
      });
    });

  const notifyDataChanged: UnicornApi['methods']['notifyDataChanged'] = (affectedData) =>
    new Promise((resolve) => {
      // FIXME: this is a temporary fix, remove once adjusted
      const correctedAffectedData = affectedData === 'productEnvironments' ? 'productEnvironment' : affectedData;
      dispatch({ type: 'dataChangeNotificationReceived', payload: { affectedData: correctedAffectedData } });

      subscribeToStateChanges(function subscriber(state) {
        if (state.applicationStatus === 'ready') {
          const requestStateMap = {
            account: 'accountFetchState',
            capabilities: 'capabilitiesFetchState',
            deprecatedData: 'deprecatedDataFetchState',
            permissions: 'permissionsFetchState',
            plan: 'planFetchState',
            productEnvironment: 'productEnvironmentFetchState',
            user: 'userFetchState',
          } as const;

          const requestState = state[requestStateMap[correctedAffectedData]];

          if (requestState.status === 'idle') {
            unsubscribeFromStateChanges(subscriber);
            resolve();
          }
        }
      });
    });

  const sendAnalytics: UnicornApi['methods']['sendAnalytics'] = (configuration) => {
    if (configuration.variant === 'universal') {
      analytics.universal(configuration.data);
    }

    if (configuration.variant === 'ga4') {
      analytics.ga4(configuration.data);
    }
  };

  const getAbTestTreatment: UnicornApi['methods']['getAbTestTreatment'] = abTestingClient;

  const createFetchClient: UnicornApi['methods']['createFetchClient'] = createCloudinaryFetchClient;

  const enableVisibilityStatus: UnicornApi['__internal']['enableVisibilityStatus'] = (details: string, justification: string) => {
    return new Promise((resolve) => {
      dispatch({ type: 'postGdprLogQueued', payload: { details, justification } });
      subscribeToStateChanges(function subscriber(state) {
        if (state.applicationStatus === 'ready' && state.postGdprLogState.status === 'idle') {
          unsubscribeFromStateChanges(subscriber);
          resolve();
        }
      });
    });
  };

  const disableVisibilityStatus: UnicornApi['__internal']['disableVisibilityStatus'] = () => {
    dispatch({ type: 'disableAssetVisibility', payload: {} });
  };

  const unicornApi: UnicornApi = {
    environmentData,
    basePath: '/',
    data: {
      impersonationActive,
      assetVisibility,
      account,
      capabilities,
      productEnvironment,
      permissions,
      user: {
        ...user,
        impersonationActive,
      },
      plan,
      deprecatedData,
      theme,
    },
    methods: {
      createFetchClient,
      notifyDataChanged,
      sendAnalytics,
      changeProductEnvironment,
      getAbTestTreatment,
      createLogger: externalLoggerFactory.createInstance,
      // HACK: only available in Unicorn host variant
      navigate: () => undefined,
    },
    __internal: {
      enableVisibilityStatus,
      disableVisibilityStatus,
    },
  };

  consoleLogger.debug('general', 'Unicorn API updated', { data: unicornApi.data, methods: Object.keys(unicornApi.methods) });

  return unicornApi;
};
